import Link from 'next/link';
import React from 'react';

import styled from '@emotion/styled';
import { useTranslations } from 'next-intl';

import { MangaIcon } from '@web/features/hub-rework/brand-icons';
import { SharpImage } from '@web/ui/components/SharpImage';
import { TileHyperLinkIcon } from '@web/ui/icons';

import { HubClasses, HubTile } from '../HubTile';

import cat from './manga-tile-cat.png';
import flowers from './manga-tile-flower.png';
import hana from './manga-tile-hana.png';
import hiroshi from './manga-tile-hiroshi.png';
import MangaLogo from './manga-tile-logo.svg';

const SHubTile = styled(HubTile)`
  overflow: hidden;
  .${HubClasses.topRowContainer} {
    align-items: flex-start;
  }
`;

const IllustrationContainer = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1 0 0;
  align-self: stretch;
  min-height: 12.5rem;

  svg {
    width: 14.625rem;
    height: 9.625rem;
    z-index: 2;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  gap: ${({ theme }) => theme.spacing('2000')};
`;

const TextContainer = styled.div`
  z-index: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-self: stretch;
  gap: ${({ theme }) => theme.spacing('0750')};
`;

const Subtitle = styled.h3`
  color: ${({ theme }) => theme.color.onSurface};
  font: ${({ theme }) => theme.typography.body.bold};
  font-size: ${({ theme }) => theme.typescale.size['4']};
  line-height: ${({ theme }) => theme.typescale.lineHeight.condensed};
`;

const Description = styled.p`
  color: ${({ theme }) => theme.color.onSurface};
  font: ${({ theme }) => theme.typography.body.regular};
  font-size: ${({ theme }) => theme.typescale.size['1']};
  line-height: ${({ theme }) => theme.typescale.lineHeight.extended};
`;

const SMangaLogo = styled(MangaLogo)`
  z-index: 1;
  color: ${({ theme }) => theme.color.onSurface};
`;

const LinkText = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing('0500')};
  color: ${({ theme }) => theme.color.onSurface};
  font: ${({ theme }) => theme.typography.mono.regular};
  font-size: ${({ theme }) => theme.typescale.size['0875']};
  line-height: ${({ theme }) => theme.typescale.lineHeight.extended};
  text-decoration: none;
  position: relative;

  &:hover {
    text-decoration: underline;
  }
`;

const Cat = styled(SharpImage)`
  position: absolute;
  width: 5.75rem;
  height: 8rem;
  bottom: -0.5rem;
  right: 5rem;
  z-index: 1;
`;

const Flowers = styled(SharpImage)`
  position: absolute;
  width: 6rem;
  height: 8rem;
  top: -1rem;
  left: 3.75rem;
  z-index: 1;
`;

const Hiroshi = styled(SharpImage)`
  position: absolute;
  width: 13.75rem;
  height: 11.875rem;
  top: -1.75rem;
  right: -1.875rem;
  z-index: 1;
`;

const Hana = styled(SharpImage)`
  position: absolute;
  width: 10.9375rem;
  height: 15.75rem;
  bottom: -1rem;
  left: 0.5rem;
  z-index: 1;
`;

export const MangaTile = () => {
  const t = useTranslations();

  return (
    <Link href="/manga">
      <SHubTile
        textColor="onSurface"
        background="surfaceContainerLow"
        stateLayer="onSurface"
        title={t('MANGA_HUB_TILE_TITLE')}
        badge={<MangaIcon size="3000" color="onSurface" />}
      >
        <IllustrationContainer>
          <SMangaLogo />
          <Hana src={hana} />
          <Hiroshi src={hiroshi} />
          <Flowers src={flowers} />
          <Cat src={cat} />
        </IllustrationContainer>

        <Content>
          <TextContainer>
            <Subtitle>{t('MANGA_HUB_TILE_SUBTITLE')}</Subtitle>
            <Description>{t('MANGA_HUB_TILE_DESCRIPTION')}</Description>
          </TextContainer>

          <LinkText href="/manga">
            {t('MANGA_HUB_TILE_BUTTON')}
            <TileHyperLinkIcon size="0750" />
          </LinkText>
        </Content>
      </SHubTile>
    </Link>
  );
};
